import { createFileRoute, Link } from "@tanstack/react-router";
import { SETTING_OUTPATIENT_ES } from "@/lib/medical-schema";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Cuánto peso pierden los pacientes con la banda gástrica (LAP-BAND)?",
    a: "En promedio, cerca del 45 por ciento del exceso de peso, frente a un 70 por ciento aproximado con la manga gástrica. La banda gástrica ajustable depende mucho de los ajustes regulares y del seguimiento a largo plazo.",
  },
  {
    q: "¿Se puede revertir la cirugía de banda gástrica?",
    a: "Sí. La banda es la única cirugía de pérdida de peso que no altera de forma permanente el tracto gástrico, así que se puede retirar por laparoscopia si es necesario.",
  },
  {
    q: "¿Con qué frecuencia hay que ajustar la banda?",
    a: "De por vida. En el primer año se espera una visita cada seis semanas aproximadamente, y con menos frecuencia después.",
  },
  {
    q: "¿Cuánto dura la cirugía de banda gástrica y cuándo puedo volver a casa?",
    a: "La operación suele durar unos 30 minutos y la mayoría de los pacientes regresa a casa el mismo día.",
  },
  {
    q: "¿Se puede convertir una banda gástrica en otra cirugía?",
    a: "Sí. La conversión a manga gástrica o a bypass gástrico es común y está bien estudiada. Puede hacerse en una sola operación o en dos etapas, según su anatomía.",
  },
  {
    q: "¿Cubre el seguro la banda gástrica?",
    a: "Muchas aseguradoras cubren la banda gástrica ajustable cuando se cumplen ciertos criterios médicos, como un IMC de 35 o más, o de 30 o más con una condición relacionada con la obesidad. Nuestro equipo verifica los beneficios y tramita la autorización.",
  },
];

export const Route = createFileRoute("/es/banda-gastrica")({
  head: () => {
    const base = esHead("band");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("band"),
              {
                "@type": "MedicalProcedure",
                "@id": `${SITE}/es/banda-gastrica#procedimiento`,
                mainEntityOfPage: { "@id": `${SITE}/es/banda-gastrica#webpage` },
                procedureType: "https://schema.org/SurgicalProcedure",
                name: "Banda gástrica ajustable (LAP-BAND)",
                description:
                  "La opción quirúrgica para bajar de peso menos invasiva y la única que puede revertirse por completo.",
                url: `${SITE}/es/banda-gastrica`,
                inLanguage: "es-US",
                bodyLocation: "Estómago",
                howPerformed:
                  "Por laparoscopía, con anestesia general, se coloca una banda de silicona ajustable alrededor de la parte superior del estómago para crear una bolsa pequeña. La banda se conecta a un puerto bajo la piel para poder ajustarla en el consultorio. " + SETTING_OUTPATIENT_ES,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
              ...procedureImageObjects(ES_PAGE_MEDIA["/es/banda-gastrica"], "es", `${SITE}/es/banda-gastrica`),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/banda-gastrica#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/banda-gastrica#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsBand,
});

function EsBand() {
  return (
    <EsPageShell
      page="band"
      title="Banda gástrica ajustable LAP-BAND® en el norte de Virginia"
      intro="La banda gástrica ajustable coloca un anillo de silicona alrededor de la parte superior del estómago para reducir cuánto come en cada comida. Es la menos invasiva de las opciones quirúrgicas para bajar de peso y la única que se puede revertir por completo. Hoy en día se coloca muy poco; si usted ya tiene una banda con problemas, el Dr. Matthew Fitzer puede retirarla o convertirla en Herndon, Virginia."
    >
      <EsSection id="que-es" heading="¿Qué es la banda gástrica?">
        <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-start">
          <div className="space-y-3">
            <p>
              La banda gástrica ajustable es un anillo de plástico de casi cuatro centímetros de
              diámetro, del tamaño aproximado de un aro de servilletero. Un tubo delgado la conecta
              a un puerto fijado a la pared del abdomen, cerca del ombligo. El puerto queda
              invisible bajo la piel incluso después de una pérdida de peso importante, y su médico
              lo alcanza con una aguja pequeña cuando llega el momento de ajustar la banda.
            </p>
            <p>
              Colocarla toma alrededor de 30 minutos y la mayoría de los pacientes regresa a casa el
              mismo día. No se corta, se grapa ni se retira nada, así que su anatomía queda casi
              intacta. La banda se puede retirar después si así lo decide. Como no se toca el
              intestino delgado, no afecta la manera en que el cuerpo absorbe los nutrientes.
            </p>
          </div>

          <ProcedureFigure
            media="bandDiagram"
            lang="es"
            sizes="(min-width: 768px) 208px, 70vw"
            className="flex flex-col items-center md:sticky md:top-4"
            imgClassName="w-full h-auto max-w-[13rem] rounded-lg"
            captionClassName="mt-2 text-center text-sm text-foreground/70"
          />
        </div>
      </EsSection>


      <EsSection id="como-funciona" heading="Cómo ayuda la banda a bajar de peso">
        <p>
          El ajuste de la banda crea una pequeña bolsa de estómago por encima de ella, del tamaño
          aproximado de una nuez. La comida que traga genera presión en esa bolsa, y esa presión
          termina empujando el bocado hacia adelante, a través de una abertura del ancho de su dedo
          meñique.
        </p>
        <p>
          Si come con calma —bocados pequeños, esperando a que pase el anterior antes de tomar
          otro—, la presión es leve y no resulta incómoda; con el tiempo activa nervios del
          estómago que envían al cerebro la señal de saciedad. Si come rápido, la presión es alta y
          resulta desagradable: puede sentir molestia bajo el esternón, ganas de vomitar o un
          exceso repentino de saliva. Comer rápido sí produce pérdida de peso a corto plazo, porque
          los episodios de alta presión quitan las ganas de comer, a veces por el resto del día.
          Pero repetir ese patrón puede terminar en un deslizamiento de la banda, una complicación
          que casi siempre obliga a retirarla.
        </p>
      </EsSection>

      <EsSection id="ajustes" heading="Ajustes y seguimiento">
        <p>
          La banda gástrica necesita apretarse o aflojarse de vez en cuando, de por vida. Al
          principio se visita la clínica cada seis semanas aproximadamente; después, con menos
          frecuencia, pero las visitas nunca terminan del todo.
        </p>
        <p>
          Quién maneja sus ajustes —cirujano, enfermera o asistente médico— influye en su resultado
          incluso más que quién colocó la banda. Si está comparando programas, pregunte quién hace
          los ajustes y con qué frecuencia lo verán.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="¿Cuánto peso se pierde con la banda?">
        <p>
          La pérdida de exceso de peso con la banda gástrica ajustable promedia alrededor del 45
          por ciento. La{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          promedia cerca del 70 por ciento, y el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>{" "}
          un poco más. Los resultados de la banda varían más que los de otras operaciones, y la
          diferencia casi siempre depende del seguimiento: quienes mantienen sus ajustes al día
          suelen tener resultados considerablemente mejores que quienes dejan de asistir después
          del primer año.
        </p>
      </EsSection>

      <EsSection id="ventajas" heading="Ventajas y desventajas">
        <p>
          A su favor: es la opción quirúrgica menos invasiva, la única reversible, casi siempre
          ambulatoria, y no afecta la absorción de nutrientes. Las complicaciones alrededor del
          momento de la cirugía son poco frecuentes.
        </p>
        <p>
          En contra: la pérdida de peso promedio es menor que con la manga o el bypass, los ajustes
          y las visitas al consultorio continúan de manera indefinida, y las reintervenciones para
          ajustar, reemplazar o retirar la banda son más comunes que con otras operaciones. Su uso
          también ha caído de forma marcada en Estados Unidos: de cerca de un tercio de todas las
          cirugías bariátricas en 2011 a bastante menos del uno por ciento en 2023.
        </p>
      </EsSection>

      <EsSection id="si-ya-tiene-banda" heading="Si usted ya tiene una banda gástrica">
        <p>
          Muchas personas viven con una banda colocada hace años. Si la suya ya no funciona bien,
          tiene opciones: se puede ajustar, o se puede retirar por laparoscopia. Muchos pacientes
          se la retiran y la convierten en manga gástrica o en bypass gástrico, en una sola
          operación o en dos. El retiro y la conversión de la banda gástrica están bien estudiados
          y, en manos experimentadas, son seguros. Nuestra página de{" "}
          <Link
            to="/es/cirugia-de-revision"
            className="font-medium text-brand-logo hover:underline"
          >
            cirugía de revisión
          </Link>{" "}
          explica ese camino con más detalle.
        </p>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Cirugía LAP-BAND y ajustes en el norte de Virginia">
        <p>
          La colocación de la LAP-BAND es casi siempre ambulatoria en INOVA Fair Oaks Hospital: la mayoría de los pacientes se van a casa el mismo día. Los ajustes de la banda se hacen en nuestro consultorio de Herndon, así que vivir cerca es importante. Los pacientes de Herndon, Reston, Fairfax y el condado de Loudoun pueden hacerse los ajustes en un día normal.
        </p>
        <p>
          ¿Está comparando opciones? Vea <Link to="/es/manga-gastrica" className="text-brand-deep underline hover:no-underline">manga gástrica en el Norte de Virginia</Link> y <Link to="/es/bypass-gastrico" className="text-brand-deep underline hover:no-underline">bypass gástrico en el Norte de Virginia</Link>. Si ya tiene una banda, vea <Link to="/es/cirugia-de-revision/banda-gastrica" className="text-brand-deep underline hover:no-underline">retiro y revisión de LAP-BAND en Virginia</Link>.
        </p>
        <p>
          ¿Preguntas sobre la LAP-BAND? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_band_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="hable-con-nosotros" heading="Conversemos sobre su caso">
        <p>
          ¿No va a usar el seguro? Consulte el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-banda-gastrica"
            className="font-medium text-brand-logo hover:underline"
          >
            costo de la banda gástrica (Lap-Band)
          </Link>{" "}
          con pago propio: un estimado de $19,200, que cubre el honorario del cirujano, un día de
          hospital en INOVA Fair Oaks Hospital, un estimado de anestesia y un año de seguimiento
          de rutina con el Dr. Fitzer.
        </p>
        <p>

          Elegir entre la banda, la manga y el bypass no es una decisión que se tome desde un sitio
          web. Cada opción se ajusta a un paciente distinto. Pida una consulta en{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contacto
          </Link>{" "}
          y revisaremos con usted todas sus opciones.
        </p>
      </EsSection>
    </EsPageShell>
  );
}
